import { Button } from '@/components/ui/button';
import { FileText } from 'lucide-react';
import KeyValueListDisplay from '@/components/KeyValueDisplay';
import type { DeliveryDetailUpdates, DeliveryUpdateEmailLog, DeliveryUpdateCard } from '@/types/deliveryDetail.types';
import { useNavigate } from 'react-router-dom';
import { convertISODateToMMDDYYYY, convertISOToTime } from '@/lib/utils';
import { hasFeature } from '@/services/featureService';
import { EmailStatus } from '@/types/types';
import { EmailLogStatusLabel } from '@/utils/constant';

export const DeliveryUpdatesContent = ({
  data,
  workOrderId,
  canEditDeliveryUpdates,
}: {
  data: DeliveryDetailUpdates | undefined;
  workOrderId?: string;
  intakeId?: string;
  clientName?: string;
  canEditDeliveryUpdates?: boolean;
}) => {
  const navigate = useNavigate();
  const isDeliveryUpdatesEnabled = hasFeature('delivery.deliveryUpdates');
  const canReadDeliveryUpdates = hasFeature('delivery.deliveryUpdates.read');

  const emailLogs = [...(data?.lastFiveDeliveryUpdateEmails ?? [])]
    .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())
    .slice(0, 5);
  const updates = data?.updates ?? [];

  if (!isDeliveryUpdatesEnabled || !canReadDeliveryUpdates || !data) {
    return null;
  }

  const navigateToDeliveryUpdate = (path: string) => {
    navigate(path);
  };

  return (
    <div className="flex flex-col gap-6 w-full px-6 py-6 text-sm">
      {emailLogs.length ? (
        <div className="flex flex-col gap-3 w-full">
          <span className="text-primary font-semibold">Emails Sent</span>
          <KeyValueListDisplay
            data={emailLogs.map((log: DeliveryUpdateEmailLog) => ({
              label: convertISODateToMMDDYYYY(log.sentAt) + ' ' + convertISOToTime(log.sentAt),
              value: EmailLogStatusLabel[log.status ?? EmailStatus.SENT],
              valueClassName:
                (log.status ?? EmailStatus.SENT) === EmailStatus.SENT ? 'text-green-700' : 'text-error',
            }))}
          />
        </div>
      ) : null}

      {/* Delivery Update Cards Section */}
      <div className="flex flex-col gap-3 w-full">
        <span className="text-primary font-semibold">Delivery Updates</span>
        {updates.length > 0 ? (
          updates.map((update: DeliveryUpdateCard) => (
            <div
              key={update.updateId}
              className="flex flex-row gap-2 justify-between p-4 rounded-lg bg-[#F5F5F5] cursor-pointer"
              onClick={() => {
                if (workOrderId) {
                  navigateToDeliveryUpdate(
                    `/delivery/work-order/${workOrderId}/delivery-update/${update.updateId}`
                  );
                }
              }}
            >
              <div className="flex flex-row gap-2">
                <div className="flex align-top">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-semibold">Delivery Updates</span>
                  <div className="flex flex-col gap-1 text-placeholder font-medium text-[12px]">
                    <span className="text-xs text-placeholder">{`Added on ${convertISODateToMMDDYYYY(update.addedAt)} ${convertISOToTime(update.addedAt)} by ${update.addedBy}`}</span>
                    {update.modifiedAt && update.modifiedBy && (
                      <span className="text-xs text-placeholder">{`Updated on ${convertISODateToMMDDYYYY(update.modifiedAt)} ${convertISOToTime(update.modifiedAt)} by ${update.modifiedBy}`}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-primary font-semibold">
                <span>Delivered</span>
              </div>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center gap-4 py-8">
            <div className="font-medium text-sm text-placeholder">No Updates</div>
            {canEditDeliveryUpdates && workOrderId && (
              <Button
                variant="default"
                onClick={() => {
                  navigateToDeliveryUpdate(`/delivery/work-order/${workOrderId}/delivery-updates?mode=add`);
                }}
              >
                Add Delivery Updates
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
